import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signIn, resetPassword } from '../../firebase/auth';
import { callFunction } from '../../firebase/functions';
import { useAuth } from '../../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import LanguageChip from '../../components/shared/LanguageChip';
import i18n from '../../i18n';
import { isPlyrmatchDomain } from '../../utils/portalDomain';
import { PlyrmatchLogo, plyrmatchButtonStyle, plyrmatchHeadingStyle, plyrmatchPageStyle } from '../../components/auth/PlyrmatchBrand';

const LOGO = `${process.env.PUBLIC_URL}/logo.png`;

function RequestModal({ onClose }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);

  const inputStyle = {
    width: '100%', height: 44, padding: '0 12px',
    border: '1px solid #e2e6e3', borderRadius: 8,
    fontSize: 14, color: '#111f1e', background: '#fafbfa',
    outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
    fontFamily: 'inherit', boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(10,25,24,0.5)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#ffffff', borderRadius: 14,
          padding: '30px 30px 26px', width: '100%', maxWidth: 400,
          boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
          border: '1px solid #e2e6e3',
          animation: 'popIn 0.22s cubic-bezier(.34,1.4,.64,1) both',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {done ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{
              width: 50, height: 50, borderRadius: '50%',
              background: '#eef7f4', margin: '0 auto 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1a3d3a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <p style={{ fontSize: 16, fontWeight: 700, color: '#111f1e', letterSpacing: '-0.02em', marginBottom: 8 }}>
              {t('auth:login.requestModal.requestReceivedTitle', 'Request received')}
            </p>
            <p style={{ fontSize: 13, color: '#3d5250', lineHeight: 1.5, marginBottom: 24 }}>
              {t('auth:login.requestModal.requestReceivedBodyPrefix', "We'll be in touch at ")}<strong>{email}</strong>{t('auth:login.requestModal.requestReceivedBodySuffix', ' within one business day.')}
            </p>
            <button
              onClick={onClose}
              style={{
                padding: '9px 28px', borderRadius: 8, border: 'none',
                background: '#1a3d3a', color: '#fff',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {t('auth:login.requestModal.done', 'Done')}
            </button>
          </div>
        ) : (
          <>
            <p style={{ fontSize: 16, fontWeight: 700, color: '#111f1e', letterSpacing: '-0.02em', marginBottom: 6 }}>
              {t('auth:login.requestModal.title', 'Request access')}
            </p>
            <p style={{ fontSize: 12.5, color: '#7a9290', lineHeight: 1.5, marginBottom: 22 }}>
              {t('auth:login.requestModal.description', "We'll review your request and be in touch about purchasing a licence for your academy.")}
            </p>

            <form onSubmit={(e) => { e.preventDefault(); setDone(true); }} style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#3d5250', letterSpacing: '0.01em', marginBottom: 6 }}>
                  {t('auth:login.requestModal.academyName', 'Academy name')}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('auth:login.requestModal.academyNamePlaceholder', 'e.g. Coastal Football Academy')}
                  style={inputStyle}
                  className="login-input"
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#3d5250', letterSpacing: '0.01em', marginBottom: 6 }}>
                  {t('auth:login.requestModal.workEmail', 'Work email')} <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth:login.requestModal.workEmailPlaceholder', 'you@academy.com')}
                  style={inputStyle}
                  className="login-input"
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    flex: 1, height: 40, borderRadius: 8,
                    border: '1px solid #e2e6e3', background: '#fafbfa',
                    color: '#3d5250', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {t('common:actions.cancel')}
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 2, height: 40, borderRadius: 8,
                    border: 'none', background: '#1a3d3a',
                    color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {t('auth:login.requestModal.sendRequest', 'Send request')}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const onPlyrmatchDomain = isPlyrmatchDomain();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      // navigation handled by the useEffect above once auth state resolves
    } catch (err) {
      setError(friendlyError(err.code));
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError(t('auth:login.enterEmailFirst'));
      return;
    }
    setResetSent(false);
    setResetError('');
    setResetLoading(true);
    try {
      if (onPlyrmatchDomain) await callFunction('requestPlyrmatchPasswordReset')({ email, lang: i18n.language });
      else await resetPassword(email);
      setResetSent(true);
    } catch {
      setResetError(t('auth:login.couldNotSendReset'));
    } finally {
      setResetLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', height: 44, padding: '0 12px',
    border: '1px solid #e2e6e3', borderRadius: 8,
    fontSize: 14, color: '#111f1e', background: '#fafbfa',
    outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
    fontFamily: 'inherit', boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f4f5f2',
        ...(onPlyrmatchDomain ? plyrmatchPageStyle : {}),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 16px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Hexagon pattern background */}
      <svg
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: onPlyrmatchDomain ? 0 : 0.035 }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="hex-bg" x="0" y="0" width="90" height="78" patternUnits="userSpaceOnUse">
            <polygon points="22.5,0 45,13 45,39 22.5,52 0,39 0,13" fill="none" stroke="#1a3d3a" strokeWidth="1.2" />
            <polygon points="67.5,39 90,52 90,78 67.5,91 45,78 45,52" fill="none" stroke="#1a3d3a" strokeWidth="1.2" />
            <polygon points="67.5,-39 90,-26 90,0 67.5,13 45,0 45,-26" fill="none" stroke="#1a3d3a" strokeWidth="1.2" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hex-bg)" />
      </svg>

      {/* Language switch */}
      <div style={{ position: 'relative', zIndex: 1, marginBottom: 16 }}>
        <LanguageChip />
      </div>

      {/* Logo above card */}
      {onPlyrmatchDomain ? (
        <PlyrmatchLogo />
      ) : (
        <img
          src={LOGO}
          alt={t('auth:login.logoAlt', 'International Academy Portal')}
          style={{ height: 64, width: 'auto', marginBottom: 24, position: 'relative', zIndex: 1 }}
        />
      )}

      {/* Card */}
      <div
        style={{
          width: '100%', maxWidth: 380,
          background: '#ffffff',
          borderRadius: 14,
          border: '1px solid #e2e6e3',
          boxShadow: '0 4px 24px rgba(26,61,58,0.07), 0 1px 4px rgba(26,61,58,0.05)',
          padding: '32px 32px 28px',
          position: 'relative', zIndex: 1,
          animation: 'fadeUp 0.3s ease both',
        }}
      >
        <h1 style={{
          fontSize: 18, fontWeight: 700, color: '#111f1e',
          letterSpacing: '-0.02em', marginBottom: 20,
          ...(onPlyrmatchDomain ? plyrmatchHeadingStyle : {}),
        }}>
          {t('auth:login.signIn', 'Sign in')}
        </h1>

        {error && (
          <div style={{
            fontSize: 12.5, color: '#922b21',
            background: '#fdf3f2', border: '1px solid #f5b7b1',
            borderRadius: 7, padding: '9px 12px', marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          {/* Email */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#3d5250', letterSpacing: '0.01em', marginBottom: 6 }}>
              {t('auth:login.emailLabel', 'Email address')}
            </label>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth:login.emailPlaceholder', 'you@example.com')}
              style={inputStyle}
              className="login-input"
            />
          </div>

          {/* Password */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#3d5250', letterSpacing: '0.01em' }}>
                {t('auth:login.passwordLabel', 'Password')}
              </label>
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={resetLoading}
                style={{
                  fontSize: 12, color: '#1a3d3a', fontWeight: 500,
                  background: 'none', border: 'none',
                  cursor: resetLoading ? 'wait' : 'pointer',
                  padding: 0, fontFamily: 'inherit', opacity: 0.8,
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.8')}
              >
                {resetLoading ? t('common:actions.sendingEllipsis') : t('auth:login.forgotPassword', 'Forgot password?')}
              </button>
            </div>

            {resetSent && (
              <div style={{
                fontSize: 12.5, color: '#1d5c56',
                background: '#eef7f4', border: '1px solid #7bc9a8',
                borderRadius: 7, padding: '8px 12px', marginBottom: 8,
              }}>
                {onPlyrmatchDomain
                  ? t('auth:login.resetLinkSentIfAccount', "If that email has an account, we've sent a reset link. Check your inbox, and use the newest email.")
                  : t('auth:login.resetLinkSent', 'Reset link sent — check your inbox.')}
              </div>
            )}
            {resetError && (
              <div style={{
                fontSize: 12.5, color: '#922b21',
                background: '#fdf3f2', border: '1px solid #f5b7b1',
                borderRadius: 7, padding: '8px 12px', marginBottom: 8,
              }}>
                {resetError}
              </div>
            )}

            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{ ...inputStyle, paddingRight: 44 }}
                className="login-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  color: '#7a9290', display: 'flex',
                }}
              >
                {showPassword
                  ? <EyeSlashIcon style={{ width: 17, height: 17 }} />
                  : <EyeIcon style={{ width: 17, height: 17 }} />
                }
              </button>
            </div>
          </div>

          {/* Sign in button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              height: 44, width: '100%', borderRadius: 8, border: 'none',
              background: loading ? '#c8d8d6' : '#1a3d3a',
              color: loading ? '#1d5c56' : '#fff',
              fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s, color 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              fontFamily: 'inherit', marginTop: 4,
              ...(onPlyrmatchDomain && !loading ? plyrmatchButtonStyle : {}),
            }}
          >
            {loading && (
              <svg style={{ width: 16, height: 16, animation: 'spin 0.7s linear infinite' }} viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeLinecap="round" />
              </svg>
            )}
            {loading ? t('auth:login.signingIn', 'Signing in…') : t('auth:login.signIn', 'Sign in')}
          </button>
        </form>

        {!onPlyrmatchDomain && (
          <>
            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '22px 0 16px' }}>
              <div style={{ flex: 1, height: 1, background: '#e2e6e3' }} />
              <span style={{ fontSize: 11.5, color: '#7a9290' }}>{t('auth:login.notMember', 'Not yet a member?')}</span>
              <div style={{ flex: 1, height: 1, background: '#e2e6e3' }} />
            </div>

            {/* Request access */}
            <button
              onClick={() => setShowModal(true)}
              style={{
                width: '100%', height: 42, borderRadius: 8,
                border: '1.5px solid #e2e6e3', background: 'transparent',
                color: '#1a3d3a', fontSize: 13.5, fontWeight: 600,
                letterSpacing: '-0.01em', cursor: 'pointer',
                transition: 'border-color 0.15s, background 0.15s',
                fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#1a3d3a';
                e.currentTarget.style.background = '#f2f7f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e2e6e3';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {t('auth:login.requestAccess', 'Request access as an academy')}
            </button>
          </>
        )}
      </div>

      {/* Footer */}
      {!onPlyrmatchDomain && (
        <p style={{
          marginTop: 22, fontSize: 11.5, color: '#7a9290', textAlign: 'center',
          position: 'relative', zIndex: 1, lineHeight: 1.6,
        }}>
          {t('auth:login.copyright', '© 2026 International Academy Portal')} ·{' '}
          <span
            style={{ cursor: 'pointer', color: '#7a9290', transition: 'color 0.15s' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#1a3d3a')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#7a9290')}
          >
            {t('auth:login.privacyPolicy', 'Privacy Policy')}
          </span>
          {' '}·{' '}
          <span
            style={{ cursor: 'pointer', color: '#7a9290', transition: 'color 0.15s' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#1a3d3a')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#7a9290')}
          >
            {t('auth:login.termsOfService', 'Terms of Service')}
          </span>
        </p>
      )}

      {showModal && <RequestModal onClose={() => setShowModal(false)} />}
    </div>
  );
}

function friendlyError(code) {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return i18n.t('auth:login.errorIncorrectCredentials', 'Incorrect email or password.');
    case 'auth/too-many-requests':
      return i18n.t('auth:login.errorTooManyRequests', 'Too many failed attempts. Please try again later.');
    case 'auth/user-disabled':
      return i18n.t('auth:login.errorUserDisabled', 'This account has been disabled.');
    default:
      return i18n.t('auth:login.errorGeneric', 'An error occurred. Please try again.');
  }
}
